import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_PRODUCTION_BATCHES, GET_BRANCHES } from "./queries";
import { ProductionBatch, PRODUCTION_BATCH_STATUS_LABELS } from "./models";
import InventoryNav from "./inventoryNav";

const btn = {
  txt: lang("Iniciar Producción", "Start Production"),
  icon: "fa fa-plus",
  link: "/inventory/production/new",
};

const ProductionBatchList: React.FC = () => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  const [fetchBatches, { data, loading }] = useLazyQuery(
    GET_PRODUCTION_BATCHES,
    { fetchPolicy: "no-cache" }
  );
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchBatches({
      variables: {
        status: statusFilter || undefined,
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
        take: 50,
      },
    });
    //eslint-disable-next-line
  }, [statusFilter, branchFilter]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  const getEfficiency = (batch: ProductionBatch) => {
    if (
      !batch.actual_output ||
      !batch.production_recipe?.output_quantity
    )
      return null;
    const expected = Number(batch.production_recipe.output_quantity);
    const actual = Number(batch.actual_output);
    if (!expected) return null;
    return ((actual / expected) * 100).toFixed(1);
  };

  return (
    <>
      <HeaderTitle
        title={lang("Producción", "Production")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="production" />

      <div className="row mb-3">
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">
              {lang("Todos los estados", "All statuses")}
            </option>
            {Object.entries(PRODUCTION_BATCH_STATUS_LABELS).map(
              ([key, val]) => (
                <option key={key} value={key}>
                  {lang(val.label, val.labelEn)}
                </option>
              )
            )}
          </select>
        </div>
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">
              {lang("Todas las sucursales", "All branches")}
            </option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3 d-flex align-items-center">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => history.push("/inventory/production/recipes")}
          >
            <i className="fa fa-book mr-1" />
            {lang("Recetas", "Recipes")}
          </button>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>{lang("Receta", "Recipe")}</th>
                    <th>{lang("Sucursal", "Branch")}</th>
                    <th>{lang("Estado", "Status")}</th>
                    <th>{lang("Inicio", "Started")}</th>
                    <th className="text-center">
                      {lang("Esperado", "Expected")}
                    </th>
                    <th className="text-center">
                      {lang("Real", "Actual")}
                    </th>
                    <th className="text-center">
                      {lang("Eficiencia", "Efficiency")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.getProductionBatches.map((batch: ProductionBatch) => {
                    const statusInfo =
                      PRODUCTION_BATCH_STATUS_LABELS[batch.status] || {
                        label: batch.status,
                        labelEn: batch.status,
                        color: "secondary",
                      };
                    const efficiency = getEfficiency(batch);

                    return (
                      <tr
                        key={batch.id}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          history.push(`/inventory/production/${batch.id}`)
                        }
                      >
                        <td className="font-weight-bold">#{batch.id}</td>
                        <td>{batch.production_recipe?.name || "—"}</td>
                        <td>{batch.branch?.name || "—"}</td>
                        <td>
                          <span className={`badge badge-${statusInfo.color}`}>
                            {lang(statusInfo.label, statusInfo.labelEn)}
                          </span>
                        </td>
                        <td>{formatDate(batch.started_at)}</td>
                        <td className="text-center">
                          {batch.production_recipe
                            ? `${Number(
                                batch.production_recipe.output_quantity
                              )} ${batch.production_recipe.output_unit}`
                            : "—"}
                        </td>
                        <td className="text-center">
                          {batch.actual_output != null
                            ? `${Number(batch.actual_output)} ${
                                batch.production_recipe?.output_unit || ""
                              }`
                            : "—"}
                        </td>
                        <td className="text-center">
                          {efficiency ? (
                            <span
                              className={
                                parseFloat(efficiency) >= 90
                                  ? "text-success font-weight-bold"
                                  : parseFloat(efficiency) >= 70
                                  ? "text-warning font-weight-bold"
                                  : "text-danger font-weight-bold"
                              }
                            >
                              {efficiency}%
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {data.getProductionBatches.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        {lang(
                          "No hay lotes de producción",
                          "No production batches"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductionBatchList;
